<!--
  Type geom + view settings bound to Passport Builder `formBase` (same object as main save).
  Mirrors InventObjectTypesGeomForm / InventObjectTypesViewsForm without Pinia useObjectSubTypeModel.
-->
<script setup lang="ts">
import { reactive, watch, watchEffect } from 'vue'
import { VColorsPicker, VIcons, VSelectSearch } from '@/shared/ui'
import { type ObjectTypeDataPayloads, useLoadTypesData } from '@/entities/Manuals/ObjectTypes'

const props = defineProps<{
  modelValue: boolean
  formBase: Record<string, any> | null
}>()

defineEmits<{
  'update:modelValue': [value: boolean]
}>()

/** Defaults aligned with {@link useObjectSubTypeModel} `fields` in ObjectTypes/model/model.ts */
function ensureTypeSettingsShape(fb: Record<string, any>) {
  if (fb.object_type_id == null && fb.object_type?.id != null) {
    fb.object_type_id = fb.object_type.id
  }
  if (fb.figure_id === undefined || fb.figure_id === null) {
    fb.figure_id = 1
  }

  if (!fb.styles || typeof fb.styles !== 'object') {
    fb.styles = {}
  }
  const st = fb.styles
  if (st.fill === undefined || st.fill === null) {
    st.fill = '#1976D23F'
  }
  if (!st.stroke || typeof st.stroke !== 'object') {
    st.stroke = {
      color: '#FF0000FF',
      width: 1,
      line_id: 1,
      dash: [],
    }
  } else {
    const sk = st.stroke
    if (sk.color === undefined || sk.color === null) sk.color = '#FF0000FF'
    if (sk.width === undefined || sk.width === null) sk.width = 1
    if (sk.line_id === undefined || sk.line_id === null) sk.line_id = 1
    if (sk.dash === undefined || sk.dash === null) sk.dash = []
  }

  if (!fb.properties || typeof fb.properties !== 'object') {
    fb.properties = {}
  }
  const p = fb.properties
  const boolKeys = [
    'has_file',
    'is_bind',
    'is_journal_replace',
    'is_must_accept',
    'is_connection_to_object',
    'is_tree',
    'is_comment',
  ] as const
  for (const k of boolKeys) {
    if (p[k] === undefined || p[k] === null) p[k] = false
  }
  if (p.zoom === undefined || p.zoom === null) p.zoom = 0
  if (p.zoom_cluster === undefined || p.zoom_cluster === null) p.zoom_cluster = 0
  if (p.description === undefined || p.description === null) p.description = ''
}

const payload = reactive<ObjectTypeDataPayloads>({
  types: [],
  figures: [],
  lines: [],
})

const typesDataQuery = useLoadTypesData()

watchEffect(() => {
  const res = typesDataQuery.data.value as { data?: ObjectTypeDataPayloads } | undefined
  if (!res?.data) return
  payload.types = res.data.types ?? []
  payload.figures = res.data.figures ?? []
  payload.lines = res.data.lines ?? []
})

watch(
  () => [props.modelValue, props.formBase] as const,
  ([open, fb]) => {
    if (open && fb) ensureTypeSettingsShape(fb)
  },
  { immediate: true },
)

const zoomChange = (property: 'zoom' | 'zoom_cluster', direction: 'up' | 'down') => {
  const fb = props.formBase
  if (!fb?.properties) return
  const zoom = Number(fb.properties[property] ?? 0)
  fb.properties[property] = direction === 'up' ? zoom + 1 : zoom - 1
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="920"
    scrollable
    content-class="pb-type-settings-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <VCard v-if="formBase" class="pb-type-settings-modal__card" rounded="xl">
      <VToolbar
        density="compact"
        color="surface"
        class="pb-type-settings-modal__toolbar px-4 px-sm-6"
        flat
      >
        <VToolbarTitle class="text-h6 font-weight-bold">
          {{ $t('itf.settings') }}
        </VToolbarTitle>
        <VSpacer />
        <VBtn icon variant="text" @click="$emit('update:modelValue', false)">
          <VIcons type="close" />
        </VBtn>
      </VToolbar>

      <VCardText class="pb-type-settings-modal__body">
        <h3 class="text-body-2 font-weight-medium mb-2">{{ $t('itf.geom_settings') }}</h3>
        <div class="form__grid_3 mt-4">
          <VSelectSearch
            :label="$t('itf.object_type')"
            :items="payload.types"
            v-model="formBase.object_type_id"
            item-value="id"
            item-title="object_name"
            :disabled="Boolean(formBase.id)"
          />

          <VSelect
            :label="$t('itf.figure_to_map')"
            :items="payload.figures"
            v-model="formBase.figure_id"
            item-value="id"
            item-title="name"
          />

          <VColorsPicker :label="$t('itf.fill_color')" v-model="formBase.styles.fill" />
        </div>

        <div class="form__grid_3">
          <VColorsPicker :label="$t('itf.stroke_color')" v-model="formBase.styles.stroke.color" />

          <VTextField
            :label="$t('itf.stroke_width')"
            v-model="formBase.styles.stroke.width"
            type="number"
            :min="1"
            :max="50"
          />

          <VSelect
            :label="$t('itf.stroke_type')"
            :items="payload.lines"
            v-model="formBase.styles.stroke.line_id"
            item-value="id"
            item-title="name"
          />
        </div>

        <div class="form__grid_3">
          <div>
            <div class="text-center">
              {{ $t('itf.zoom_hide_object') }}
            </div>

            <VSlider
              :min="0"
              :max="22"
              :step="1"
              color="primary"
              thumb-label
              append-icon="add"
              prepend-icon="check_indeterminate_small"
              density="compact"
              @click:append="zoomChange('zoom', 'up')"
              @click:prepend="zoomChange('zoom', 'down')"
              v-model="formBase.properties.zoom"
            >
              <template #details>
                <div v-if="formBase.properties.zoom > 21" class="pb-type-settings__slider-hint">
                  {{ $t('itf.not_visible_to_cart') }}
                </div>
              </template>
            </VSlider>
          </div>

          <div>
            <div class="text-center">
              {{ $t('itf.zoom_claster_object') }}
            </div>

            <VSlider
              :min="0"
              :max="22"
              :step="1"
              color="primary"
              thumb-label
              append-icon="add"
              prepend-icon="check_indeterminate_small"
              density="compact"
              @click:append="zoomChange('zoom_cluster', 'up')"
              @click:prepend="zoomChange('zoom_cluster', 'down')"
              v-model="formBase.properties.zoom_cluster"
            />
          </div>
        </div>

        <VDivider class="my-4" />

        <h3 class="text-body-2 font-weight-medium mb-2">{{ $t('itf.view_settings') }}</h3>
        <div class="form__grid_3 form__grid_top mt-4">
          <div class="ml-2">
            <VCheckbox
              :label="$t('itf.has_file')"
              v-model="formBase.properties.has_file"
              density="compact"
            />

            <VCheckbox
              :label="$t('itf.is_bind')"
              v-model="formBase.properties.is_bind"
              density="compact"
            />

            <VCheckbox
              :label="$t('itf.is_journal_replace')"
              v-model="formBase.properties.is_journal_replace"
              density="compact"
            />

            <VCheckbox
              :label="$t('itf.is_must_accept')"
              v-model="formBase.properties.is_must_accept"
              density="compact"
            />
          </div>

          <div>
            <VCheckbox
              :label="$t('itf.is_connection_to_object')"
              v-model="formBase.properties.is_connection_to_object"
              density="compact"
            />

            <VCheckbox
              :label="$t('itf.is_tree')"
              v-model="formBase.properties.is_tree"
              density="compact"
            />

            <VCheckbox
              :label="$t('itf.is_comment')"
              v-model="formBase.properties.is_comment"
              density="compact"
            />
          </div>

          <VTextarea :label="$t('itf.description')" v-model="formBase.properties.description" />
        </div>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.pb-type-settings__slider-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: var(--red-error);
}

.pb-type-settings-modal__card {
  overflow: hidden;
  border-radius: 16px;
}

/* Toolbar background must follow card top radius (otherwise top corners look square). */
.pb-type-settings-modal__toolbar {
  border-radius: 16px 16px 0 0;
  overflow: hidden;
}

/*
 * VDialog scrollable forces v-card-text { padding: 0 } — restore spacing and separate from header.
 */
.pb-type-settings-modal__body {
  max-height: min(78vh, 720px);
  padding: 16px !important;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

@media (min-width: 600px) {
  .pb-type-settings-modal__body {
    padding: 24px !important;
  }
}
</style>

<style lang="scss">
/* VDialog applies content-class to v-overlay__content — teleported, not scoped */
.pb-type-settings-dialog {
  padding: 16px;
  box-sizing: border-box;
}
</style>
